import { getBuildVersion, getSentryEnvironment } from '@/lib/config';
import { buildLogTimeline } from '@/lib/diagnostics/buildLogTimeline';
import {
  type ConnectivityResult,
  runConnectivityCheck,
} from '@/lib/diagnostics/connectivity';
import type { LogTimelineEntry } from '@/lib/diagnostics/logTimeline';
import {
  getRecentRequests,
  redactRequestPath,
  type RequestLogEntry,
} from '@/lib/diagnostics/requestLog';

/** Where in the product the report was initiated from. */
export type DiagnosticsTrigger =
  | 'help_menu'
  | 'load_error:network'
  | 'load_error:server'
  | 'load_error:auth'
  | 'error_page';

export type ScreenshotFormat = 'webp' | 'jpeg' | 'png';

export type ScreenshotCaptureStatus =
  | 'included'
  | 'declined'
  | 'unsupported'
  | 'failed'
  | 'too_large';

export interface DiagnosticsScreenshot {
  dataUri: string;
  format: ScreenshotFormat;
  byteLength: number;
  width: number;
  height: number;
}

export interface DiagnosticsCapture {
  logs: 'included' | 'not_offered' | 'dropped_by_server';
  internalReporter: boolean;
  /** Added downstream in the modal once capture resolves — absent at collection time. */
  screenshot?: ScreenshotCaptureStatus;
}

export interface DiagnosticsBundle {
  schemaVersion: 2;
  collectedAt: string;
  trigger: DiagnosticsTrigger;
  page: {
    /** Redacted pathname of the page the report was collected on. */
    path: string;
  };
  connectivity: {
    reachable: boolean;
    outcome: ConnectivityResult['outcome'];
    /** Present only when a response actually arrived. */
    httpStatus?: number;
    /** Time until the probe succeeded OR failed — not latency on failure. */
    timeToResultMs: number;
    deviceOnline: boolean;
  };
  /**
   * 'active' — a valid session responded; 'none' — the server said the
   * session is gone; 'unknown' — the session endpoint was unreachable, so
   * nothing can be concluded.
   */
  session: 'active' | 'none' | 'unknown';
  build: {
    version: string;
    environment: string;
  };
  browser: {
    userAgent: string;
    language: string;
    platform: string;
    viewport: { width: number; height: number };
  };
  recentRequests: RequestLogEntry[];
  logs?: { entries: LogTimelineEntry[] };
  /** Present only when a screenshot was captured and encoded within budget. Owned by this issue. */
  screenshot?: DiagnosticsScreenshot;
  /** Capture manifest. Created by this issue; extended by sibling issues. */
  capture?: DiagnosticsCapture;
}

export interface CollectBundleOptions {
  isInternalReporter?: boolean;
}

function deriveSession(
  sessionStatus: ConnectivityResult['sessionStatus']
): DiagnosticsBundle['session'] {
  if (sessionStatus === 200) {
    return 'active';
  }
  if (sessionStatus === 401 || sessionStatus === 410) {
    return 'none';
  }
  return 'unknown';
}

function getCurrentPagePath(): string {
  try {
    return redactRequestPath(window.location.pathname);
  } catch {
    return '';
  }
}

export async function collectDiagnosticsBundle(
  trigger: DiagnosticsTrigger,
  options: CollectBundleOptions = {}
): Promise<DiagnosticsBundle> {
  const probe = await runConnectivityCheck();

  const connectivity: DiagnosticsBundle['connectivity'] = {
    reachable: probe.reachable,
    outcome: probe.outcome,
    timeToResultMs: probe.latencyMs,
    deviceOnline: probe.online,
  };
  if (probe.httpStatus !== null) {
    connectivity.httpStatus = probe.httpStatus;
  }

  const build = {
    version: getBuildVersion() ?? 'unknown',
    environment: getSentryEnvironment(),
  };

  const browser = {
    userAgent:
      typeof navigator !== 'undefined' ? (navigator?.userAgent ?? '') : '',
    language:
      typeof navigator !== 'undefined' ? (navigator?.language ?? '') : '',
    platform:
      typeof navigator !== 'undefined' ? (navigator?.platform ?? '') : '',
    viewport: {
      width: typeof window !== 'undefined' ? (window?.innerWidth ?? 0) : 0,
      height: typeof window !== 'undefined' ? (window?.innerHeight ?? 0) : 0,
    },
  };

  const recentRequests = getRecentRequests();

  const isInternalReporter = options.isInternalReporter === true;
  let logs: DiagnosticsBundle['logs'];
  let logsStatus: 'included' | 'not_offered' = 'not_offered';
  if (isInternalReporter) {
    const entries = buildLogTimeline();
    if (entries.length > 0) {
      logs = { entries };
      logsStatus = 'included';
    }
  }
  const capture: DiagnosticsBundle['capture'] = {
    logs: logsStatus,
    internalReporter: isInternalReporter,
  };

  return {
    schemaVersion: 2,
    collectedAt: new Date().toISOString(),
    trigger,
    page: { path: getCurrentPagePath() },
    connectivity,
    session: deriveSession(probe.sessionStatus),
    build,
    browser,
    recentRequests,
    capture,
    ...(logs && { logs }),
  };
}
